import React, { useState } from 'react';
import { MobileChart } from './mobile/MobileChart';
import { TradingViewChart } from '../../../plugins/charts/TradingViewChart';
import './ChartSection.css';

interface ChartSectionProps {
  isMobile: boolean;
  selectedInstrument: string;
  currentPrice: string;
  selectedProduct: string;
  onInstrumentSelect: () => void;
  onExpandChart: () => void;
}

export const ChartSection: React.FC<ChartSectionProps> = ({
  isMobile,
  selectedInstrument,
  currentPrice,
  selectedProduct,
  onInstrumentSelect,
  onExpandChart
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'1s' | '1m' | '5m' | '15m' | '1h' | '1d'>('5m');
  const [chartType, setChartType] = useState<'candlestick' | 'line'>('candlestick');

  // Determine if chart should be visible based on product type (matches wireframe)
  const isChartVisible = () => {
    // Chart is hidden for digits products according to wireframe
    const digitsProducts = ['matches-differs', 'even-odd', 'over-under'];
    return !digitsProducts.includes(selectedProduct);
  };

  const handleTimeframeChange = (timeframe: string) => {
    setSelectedTimeframe(timeframe as '1s' | '1m' | '5m' | '15m' | '1h' | '1d');
  };

  const handleChartTypeChange = (newChartType: 'candlestick' | 'line') => {
    setChartType(newChartType);
    
    // If switching to candlestick and current timeframe is 1s, change to 1m
    // since candles API doesn't support 1s interval
    if (newChartType === 'candlestick' && selectedTimeframe === '1s') {
      setSelectedTimeframe('1m');
    }
  };

  // Get available timeframes based on chart type
  const getAvailableTimeframes = () => {
    const allTimeframes = [
      { value: '1s', label: '1s' },
      { value: '1m', label: '1m' },
      { value: '5m', label: '5m' },
      { value: '15m', label: '15m' },
      { value: '1h', label: '1h' },
      { value: '1d', label: '1d' }
    ];

    // For candlestick charts, exclude 1s since API doesn't support it
    if (chartType === 'candlestick') {
      return allTimeframes.filter(tf => tf.value !== '1s');
    }
    
    // For line charts, include all timeframes (1s uses ticks data)
    return allTimeframes;
  };

  // Convert selectedInstrument ID to display symbol
  const getInstrumentSymbol = (instrumentId: string): string => {
    // Return the instrument ID as-is since we no longer have static mappings
    // In a real implementation, this would fetch the symbol from the instrument service
    return instrumentId || 'No instrument selected';
  };

  return (
    <>
      {/* Chart Section - Mobile vs Desktop */}
      {isMobile ? (
        <MobileChart
          selectedInstrument={selectedInstrument}
          currentPrice={currentPrice}
          isVisible={isChartVisible()}
          onInstrumentSelect={onInstrumentSelect}
          onExpandChart={onExpandChart}
        />
      ) : (
        <section className="chart-section">
          <div className="chart-header">
            <div className="chart-instrument">{getInstrumentSymbol(selectedInstrument)} - {currentPrice}</div>
            <div className="timeframe-controls">
              {getAvailableTimeframes().map((timeframe) => (
                <button 
                  key={timeframe.value}
                  className={`timeframe-btn ${selectedTimeframe === timeframe.value ? 'active' : ''}`}
                  onClick={() => handleTimeframeChange(timeframe.value)}
                >
                  {timeframe.label}
                </button>
              ))}
            </div>
          </div>
          <div className="chart-container">
            <div className="chart-canvas">
              {!selectedInstrument ? (
                <div style={{
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#1a1a1a',
                  color: '#666',
                  border: '1px solid #2a2a2a'
                }}>
                  Please select an instrument to view chart
                </div>
              ) : !isChartVisible() ? (
                <div style={{
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#1a1a1a',
                  color: '#666',
                  border: '1px solid #2a2a2a'
                }}>
                  Chart not available for digit-based products
                </div>
              ) : (
                <TradingViewChart
                  instrumentId={selectedInstrument}
                  timeframe={selectedTimeframe}
                  chartType={chartType}
                  height={400}
                  onTimeframeChange={handleTimeframeChange}
                  onChartTypeChange={handleChartTypeChange}
                />
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
};
